import {
  CASCADE_OFFSET,
  MIN_WINDOW_HEIGHT,
  MIN_WINDOW_WIDTH,
  MOBILE_BREAKPOINT,
  MOBILE_WINDOW_MARGIN,
  TASKBAR_HEIGHT,
  WINDOW_BY_ID,
  WINDOWS,
} from "../data/windows";

export const getDesktopSize = () => ({
  width: window.innerWidth,
  height: Math.max(0, window.innerHeight - TASKBAR_HEIGHT),
});

export const isMobileViewport = () => window.innerWidth <= MOBILE_BREAKPOINT;

export const getMaximizedRect = () => {
  const desktop = getDesktopSize();

  return {
    x: 0,
    y: 0,
    width: desktop.width,
    height: desktop.height,
  };
};

export const getDefaultSize = (id) => {
  const meta = WINDOW_BY_ID[id];

  if (isMobileViewport()) {
    const desktop = getDesktopSize();
    const maxWidth = Math.max(
      MIN_WINDOW_WIDTH,
      desktop.width - MOBILE_WINDOW_MARGIN * 2
    );
    const maxHeight = Math.max(
      MIN_WINDOW_HEIGHT,
      desktop.height - MOBILE_WINDOW_MARGIN * 2
    );

    return {
      width: Math.min(meta.tabletWidth, maxWidth),
      height: Math.min(meta.tabletHeight, maxHeight),
    };
  }

  if (
    window.innerWidth <= 1500 &&
    window.innerWidth > MOBILE_BREAKPOINT
  ) {
    return {
      width: meta.tabletWidth,
      height: meta.tabletHeight,
    };
  }

  return {
    width: meta.defaultWidth,
    height: meta.defaultHeight,
  };
};

export const clampPosition = (x, y, width, height) => {
  const desktop = getDesktopSize();
  const titleVisible = 48;

  return {
    x: Math.min(Math.max(x, -width + titleVisible), desktop.width - titleVisible),
    y: Math.min(Math.max(y, 0), Math.max(0, desktop.height - titleVisible)),
  };
};

export const fitRectToDesktop = (rect, margin = 0) => {
  if (!rect) {
    return null;
  }

  const desktop = getDesktopSize();
  const maxWidth = Math.max(MIN_WINDOW_WIDTH, desktop.width - margin * 2);
  const maxHeight = Math.max(MIN_WINDOW_HEIGHT, desktop.height - margin * 2);
  const width = Math.max(
    Math.min(rect.width, maxWidth),
    Math.min(MIN_WINDOW_WIDTH, maxWidth)
  );
  const height = Math.max(
    Math.min(rect.height, maxHeight),
    Math.min(MIN_WINDOW_HEIGHT, maxHeight)
  );

  return {
    x: Math.min(
      Math.max(margin, rect.x),
      Math.max(margin, desktop.width - margin - width)
    ),
    y: Math.min(
      Math.max(margin, rect.y),
      Math.max(margin, desktop.height - margin - height)
    ),
    width,
    height,
  };
};

export const fitWindowToViewport = (win) => {
  const margin = isMobileViewport() ? MOBILE_WINDOW_MARGIN : 0;
  const restoreRect = fitRectToDesktop(
    win.restoreRect || (win.isMaximized ? getDefaultRect(win.id) : null),
    margin
  );

  if (win.isMaximized) {
    return {
      ...win,
      ...getMaximizedRect(),
      isMaximized: true,
      restoreRect,
    };
  }

  return {
    ...win,
    ...fitRectToDesktop(win, margin),
    restoreRect,
  };
};

export const getDefaultRect = (id) => {
  const desktop = getDesktopSize();
  const index = WINDOWS.findIndex((item) => item.id === id);
  const size = getDefaultSize(id);
  const width = Math.min(size.width, desktop.width);
  const height = Math.min(size.height, desktop.height);
  const offset = isMobileViewport() ? 0 : CASCADE_OFFSET;
  const x = (desktop.width - width) / 2 + index * offset;
  const y = (desktop.height - height) / 2 + index * offset;

  return {
    width,
    height,
    ...clampPosition(x, y, width, height),
  };
};

export const clampResize = (origin, proposed, edge) => {
  const desktop = getDesktopSize();
  let { x, y, width, height } = proposed;

  width = Math.max(MIN_WINDOW_WIDTH, width);
  height = Math.max(MIN_WINDOW_HEIGHT, height);

  if (edge.includes("w")) {
    x = origin.x + origin.width - width;

    if (x < 0) {
      width += x;
      x = 0;
    }
  }

  if (edge.includes("n")) {
    y = origin.y + origin.height - height;

    if (y < 0) {
      height += y;
      y = 0;
    }
  }

  if (edge.includes("e") && x + width > desktop.width) {
    width = desktop.width - x;
  }

  if (edge.includes("s") && y + height > desktop.height) {
    height = desktop.height - y;
  }

  return {
    x,
    y,
    width: Math.max(MIN_WINDOW_WIDTH, width),
    height: Math.max(MIN_WINDOW_HEIGHT, height),
  };
};

export const createWindowState = (id, status, zIndex) => ({
  id,
  status,
  ...getDefaultRect(id),
  zIndex,
  isMaximized: false,
  restoreRect: null,
});

export const getFocusedId = (windows) => {
  let focusedId = null;
  let maxZ = -Infinity;

  Object.values(windows).forEach((win) => {
    if (win.status === "open" && win.zIndex > maxZ) {
      maxZ = win.zIndex;
      focusedId = win.id;
    }
  });

  return focusedId;
};

export const nextZIndex = (windows) =>
  Math.max(0, ...Object.values(windows).map((win) => win.zIndex)) + 1;
